<template>
  <!-- Block Editor overlay (full-screen) -->
  <LandingBlockEditor
    v-if="blockEditorPage"
    :pageId="blockEditorPage.id"
    :pageTitle="blockEditorPage.title"
    :pageSlug="blockEditorPage.slug"
    :initialBlocks="blockEditorPage.initialBlocks || []"
    :variantId="blockEditorPage.variantId || null"
    @close="closeBlockEditor"
    @saved="() => { closeBlockEditor(); load(); if (drawerTab === 'ab' && selectedPage) loadVariants(selectedPage.id) }"
  />

  <!-- ─────────────── Template Picker Modal ─────────────── -->
  <div v-if="showTemplatePicker" class="fixed inset-0 flex items-center justify-center z-50" style="background:rgba(0,0,0,.5);">
    <div class="card" style="width:1040px;max-width:96vw;max-height:92vh;display:flex;flex-direction:column;">

      <!-- Header -->
      <div class="flex items-center justify-between p-5" style="border-bottom:1px solid var(--border);flex-shrink:0;">
        <div>
          <h2 class="font-semibold text-lg">新增行銷推廣頁</h2>
          <p class="text-xs mt-0.5" style="color:var(--gray-500);">選擇模板或讓 AI 自動生成頁面內容</p>
        </div>
        <button @click="showTemplatePicker = false" style="font-size:1.25rem;background:none;border:none;cursor:pointer;color:var(--gray-400);">✕</button>
      </div>

      <!-- 模式切換 -->
      <div class="px-5 pt-4 pb-0" style="flex-shrink:0;">
        <div class="tpl-mode-tabs">
          <button :class="['tpl-mode-btn', pickerMode === 'template' && 'active']" @click="pickerMode = 'template'">
            📋 選擇模板
          </button>
          <button :class="['tpl-mode-btn', pickerMode === 'ai' && 'active']" @click="pickerMode = 'ai'">
            ✨ AI 自動生成
          </button>
        </div>
      </div>

      <!-- 共用：標題 + 來源活動 -->
      <div class="px-5 pt-3 pb-2 flex gap-4" style="flex-shrink:0;flex-wrap:wrap;">
        <div style="flex:1;min-width:200px;">
          <label class="form-label">頁面標題</label>
          <input v-model="newPageTitle" class="input" placeholder="例：2026 新品發表會" @keydown.enter.prevent />
        </div>
        <div style="flex:1;min-width:200px;">
          <label class="form-label">來源活動 <span style="color:var(--gray-400);font-weight:400;">（選填）</span></label>
          <select v-model="pickerCampaignId" class="input">
            <option value="">不指定</option>
            <option v-for="c in campaigns" :key="c.id" :value="c.id">#{{ c.id }} {{ c.subject || '（無主旨）' }}</option>
          </select>
        </div>
      </div>

      <!-- AI 生成欄位 -->
      <div v-if="pickerMode === 'ai'" class="px-5 pb-3 space-y-3" style="flex-shrink:0;">
        <div>
          <label class="form-label">描述這個頁面要賣什麼 / 推廣什麼 <span style="color:#dc2626;">*</span></label>
          <textarea v-model="aiDescription" class="input" rows="3"
            placeholder="例：我們提供企業 AI 導入顧問服務，幫助中小企業用 AI 提升效率、降低人力成本，主要面向製造業和零售業的管理者…"></textarea>
        </div>
        <div class="flex gap-3">
          <div style="flex:1;">
            <label class="form-label">目標受眾 <span style="color:var(--gray-400);font-weight:400;">（選填）</span></label>
            <input v-model="aiTargetAudience" class="input" placeholder="例：中小企業主、30-50 歲、關心降低成本" />
          </div>
          <div style="flex:0 0 160px;">
            <label class="form-label">文案語氣</label>
            <select v-model="aiTone" class="input">
              <option value="professional">專業正式</option>
              <option value="casual">輕鬆親切</option>
              <option value="enthusiastic">熱情有活力</option>
            </select>
          </div>
        </div>
        <div class="tpl-ai-hint">
          💡 AI 將根據描述自動生成主視覺文案、三大特色、行動呼籲區塊和表單，生成後可繼續在編輯器調整
        </div>
      </div>

      <!-- 模板卡片（模板模式） -->
      <div v-if="pickerMode === 'template'" class="p-5 overflow-y-auto flex-1">
        <div class="tpl-grid">
          <div
            v-for="tpl in LP_TEMPLATES"
            :key="tpl.id"
            :class="['tpl-card', pickerSelectedId === tpl.id && 'tpl-card-selected']"
            @click="pickerSelectedId = tpl.id"
          >
            <div class="tpl-tower">
              <div v-for="(seg, si) in tpl.segments" :key="si"
                class="tpl-seg"
                :style="{ background: seg.color, height: seg.h + 'px', opacity: seg.op || 1 }">
                <span v-if="seg.label" class="tpl-seg-label">{{ seg.label }}</span>
              </div>
            </div>
            <div class="tpl-info">
              <div class="tpl-icon">{{ tpl.icon }}</div>
              <div class="tpl-name">{{ tpl.name }}</div>
              <div class="tpl-desc">{{ tpl.desc }}</div>
            </div>
            <div v-if="pickerSelectedId === tpl.id" class="tpl-check">✓</div>
          </div>
        </div>
      </div>

      <!-- AI 模式：空白區（說明） -->
      <div v-if="pickerMode === 'ai'" class="flex-1" />

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-5" style="border-top:1px solid var(--border);flex-shrink:0;">
        <button class="btn btn-secondary" @click="showTemplatePicker = false">取消</button>
        <button v-if="pickerMode === 'template'"
          class="btn btn-primary" :disabled="!newPageTitle.trim() || creatingPage" @click="createFromTemplate">
          {{ creatingPage ? '建立中…' : '建立並開始編輯 →' }}
        </button>
        <button v-if="pickerMode === 'ai'"
          class="btn btn-ai" :disabled="!newPageTitle.trim() || !aiDescription.trim() || creatingPage" @click="createWithAI">
          {{ creatingPage ? '✨ AI 生成中…' : '✨ AI 自動生成頁面 →' }}
        </button>
      </div>
    </div>
  </div>

  <div class="p-6 space-y-6">

    <!-- Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">行銷推廣頁面管理</h1>
        <p class="page-subtitle">建立、編輯行銷落地頁，追蹤瀏覽與表單轉換率</p>
      </div>
      <div class="flex gap-2">
        <button class="btn btn-secondary btn-sm" @click="load" :disabled="loading">重新整理</button>
        <button class="btn btn-primary" @click="openCreate">+ 新增行銷推廣頁</button>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-2 gap-4" style="grid-template-columns:repeat(4,1fr);">
      <div class="card p-5 text-center">
        <div class="text-2xl font-bold" style="color:var(--brand-600);">{{ pages.length }}</div>
        <div class="text-sm mt-1" style="color:var(--gray-500);">頁面總數</div>
      </div>
      <div class="card p-5 text-center">
        <div class="text-2xl font-bold" style="color:var(--brand-600);">{{ totalViews.toLocaleString() }}</div>
        <div class="text-sm mt-1" style="color:var(--gray-500);">總瀏覽數</div>
      </div>
      <div class="card p-5 text-center">
        <div class="text-2xl font-bold" style="color:var(--color-success);">{{ totalConversions.toLocaleString() }}</div>
        <div class="text-sm mt-1" style="color:var(--gray-500);">總轉換數</div>
      </div>
      <div class="card p-5 text-center">
        <div class="text-2xl font-bold" :style="{ color: avgConvRate >= 5 ? 'var(--color-success)' : avgConvRate >= 2 ? 'var(--color-warning)' : 'var(--gray-600)' }">
          {{ avgConvRate.toFixed(1) }}%
        </div>
        <div class="text-sm mt-1" style="color:var(--gray-500);">平均轉換率</div>
      </div>
    </div>

    <!-- Loading / Empty -->
    <div v-if="loading" class="card p-10 text-center" style="color:var(--gray-400);">載入中…</div>
    <div v-else-if="!pages.length" class="card p-10 text-center space-y-3">
      <div style="color:var(--gray-400);font-size:2rem;">📄</div>
      <p style="color:var(--gray-500);">尚無行銷推廣頁</p>
      <button class="btn btn-primary btn-sm" @click="openCreate">建立第一個頁面</button>
    </div>

    <!-- Pages Table -->
    <div v-else class="card overflow-hidden">
      <table class="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>標題</th>
            <th>公開連結</th>
            <th>來源活動</th>
            <th>建立時間</th>
            <th style="text-align:center;">瀏覽</th>
            <th style="text-align:center;">轉換</th>
            <th style="text-align:center;">轉換率</th>
            <th style="text-align:right;">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in pages" :key="p.id">
            <td class="text-sm" style="color:var(--gray-400);">{{ p.id }}</td>
            <td>
              <div class="flex items-center gap-1.5">
                <div class="font-medium text-sm">{{ p.title }}</div>
                <span v-if="p.status === 'draft'" class="badge" style="background:#fffbeb;color:#b45309;border:1px solid #fcd34d;font-size:.65rem;">草稿</span>
              </div>
            </td>
            <td>
              <div class="flex items-center gap-1.5">
                <a :href="publicUrl(p)" target="_blank"
                  class="text-xs truncate" style="max-width:140px;color:var(--brand-500);"
                  :title="publicUrl(p)">
                  /p/{{ p.slug }}
                </a>
                <button class="btn btn-secondary" style="padding:2px 6px;font-size:0.65rem;height:auto;" @click="copyUrl(p)" title="複製連結">
                  複製
                </button>
              </div>
            </td>
            <td class="text-sm" style="color:var(--gray-500);">
              {{ p.campaign_id ? '#' + p.campaign_id : '—' }}
            </td>
            <td class="text-xs" style="color:var(--gray-500);">{{ fmtDate(p.created_at) }}</td>
            <td style="text-align:center;">
              <span class="font-semibold text-sm">{{ (p.views || 0).toLocaleString() }}</span>
            </td>
            <td style="text-align:center;">
              <span class="font-semibold text-sm" style="color:var(--color-success);">{{ (p.conversions || 0).toLocaleString() }}</span>
            </td>
            <td style="text-align:center;">
              <span class="badge"
                :class="convRate(p) >= 5 ? 'badge-success' : convRate(p) >= 2 ? 'badge-warning' : ''">
                {{ convRate(p).toFixed(1) }}%
              </span>
            </td>
            <td>
              <div class="flex justify-end gap-1.5">
                <button class="btn btn-secondary btn-sm" @click="openSubmissions(p)" title="表單提交記錄">提交記錄</button>
                <button class="btn btn-secondary btn-sm" @click="openBlockEditor(p)">編輯</button>
                <button class="btn btn-sm" style="background:var(--color-danger-bg);color:var(--color-danger);border:1px solid var(--color-danger);" @click="confirmDelete(p)">刪除</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ─────────────── Stats / Submissions Drawer ─────────────── -->
    <div v-if="showSubmissions" class="fixed inset-0 flex items-end justify-end z-50" style="background:rgba(0,0,0,0.35);" @click.self="showSubmissions = false">
      <div class="card" style="width:600px;max-width:96vw;height:100vh;display:flex;flex-direction:column;border-radius:0;">
        <!-- Header -->
        <div class="flex items-center justify-between p-4" style="border-bottom:1px solid var(--border);flex-shrink:0;">
          <div>
            <h2 class="font-semibold">{{ selectedPage?.title }}</h2>
            <p class="text-xs mt-0.5" style="color:var(--gray-500);">/p/{{ selectedPage?.slug }}</p>
          </div>
          <button @click="showSubmissions = false" style="font-size:1.25rem;background:none;border:none;cursor:pointer;color:var(--gray-400);">✕</button>
        </div>

        <!-- Tabs -->
        <div class="flex" style="border-bottom:1px solid var(--border);flex-shrink:0;">
          <button :class="['drawer-tab', drawerTab === 'stats' && 'active']" @click="drawerTab = 'stats'">📊 數據統計</button>
          <button :class="['drawer-tab', drawerTab === 'ab' && 'active']" @click="openAbTab">🔀 A/B 測試</button>
          <button :class="['drawer-tab', drawerTab === 'subs' && 'active']" @click="drawerTab = 'subs'">📋 提交記錄</button>
        </div>

        <!-- Stats tab -->
        <div v-if="drawerTab === 'stats'" class="p-5 overflow-y-auto flex-1">
          <div v-if="statsLoading" class="text-center py-10" style="color:var(--gray-400);">載入中…</div>
          <template v-else>
            <LandingStatsChart :stats="pageStats" :period="statsPeriod" @period="switchPeriod" />

            <!-- 轉換漏斗 (P5-2) -->
            <div v-if="pageAnalytics.funnel" class="mt-6">
              <div class="font-semibold text-sm mb-3" style="color:var(--gray-700);">🫧 轉換漏斗（近 {{ pageAnalytics.period_days }} 天）</div>
              <div class="space-y-2">
                <div v-for="(f, i) in pageAnalytics.funnel" :key="i">
                  <div class="flex justify-between text-xs mb-1" style="color:var(--gray-600);">
                    <span>{{ f.step }}</span>
                    <span><strong>{{ f.count.toLocaleString() }}</strong> · {{ f.rate }}%</span>
                  </div>
                  <div style="height:22px;background:var(--gray-100,#f1f5f9);border-radius:6px;overflow:hidden;">
                    <div :style="{ width: Math.max(2, f.rate) + '%', height:'100%', background: ['#6366f1','#8b5cf6','#22c55e'][i] || '#6366f1', transition:'width .4s' }"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 流量來源 (P5-3) -->
            <div v-if="pageAnalytics.sources && pageAnalytics.sources.length" class="mt-6">
              <div class="font-semibold text-sm mb-3" style="color:var(--gray-700);">🌐 流量來源</div>
              <div class="space-y-1">
                <div v-for="s in pageAnalytics.sources" :key="s.label" class="flex items-center justify-between text-sm py-1" style="border-bottom:1px solid var(--border);">
                  <span style="color:var(--gray-700);">{{ s.label || '（不明）' }}</span>
                  <span class="font-semibold" style="color:var(--gray-600);">{{ s.count.toLocaleString() }}</span>
                </div>
              </div>
            </div>

            <!-- CTA 點擊 (P5-1) -->
            <div v-if="pageAnalytics.cta_clicks && pageAnalytics.cta_clicks.length" class="mt-6">
              <div class="font-semibold text-sm mb-3" style="color:var(--gray-700);">🖱 按鈕點擊（共 {{ (pageAnalytics.cta_clicks_total||0).toLocaleString() }} 次）</div>
              <div class="space-y-1">
                <div v-for="c in pageAnalytics.cta_clicks" :key="c.label" class="flex items-center justify-between text-sm py-1" style="border-bottom:1px solid var(--border);">
                  <span style="color:var(--gray-700);">{{ c.label || '（按鈕）' }}</span>
                  <span class="font-semibold" style="color:var(--gray-600);">{{ c.count.toLocaleString() }}</span>
                </div>
              </div>
            </div>

            <!-- 捲動深度 (P5-1) -->
            <div v-if="pageAnalytics.scroll" class="mt-6 mb-2">
              <div class="font-semibold text-sm mb-3" style="color:var(--gray-700);">📜 捲動深度</div>
              <div class="flex gap-2">
                <div v-for="s in pageAnalytics.scroll" :key="s.depth" class="flex-1 text-center">
                  <div style="height:70px;background:var(--gray-100,#f1f5f9);border-radius:6px;display:flex;align-items:flex-end;overflow:hidden;">
                    <div :style="{ width:'100%', height: Math.max(3, s.rate) + '%', background:'#0ea5e9', transition:'height .4s' }"></div>
                  </div>
                  <div class="text-xs mt-1" style="color:var(--gray-600);">{{ s.depth }}%<br><span style="color:var(--gray-400);">{{ s.count }}</span></div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- A/B Testing tab -->
        <div v-if="drawerTab === 'ab'" class="p-5 overflow-y-auto flex-1">
          <div v-if="abLoading" class="text-center py-10" style="color:var(--gray-400);">載入中…</div>
          <template v-else>
            <!-- Enable toggle -->
            <div class="flex items-center justify-between mb-4 pb-4" style="border-bottom:1px solid var(--border);">
              <div>
                <div class="font-medium text-sm">A/B 測試</div>
                <div class="text-xs mt-0.5" style="color:var(--gray-500);">啟用後系統依流量比例隨機展示不同版本</div>
              </div>
              <label class="lp-toggle">
                <input type="checkbox" v-model="abEnabled" @change="toggleAbEnabled" />
                <span class="lp-toggle-slider"></span>
              </label>
            </div>

            <!-- Variants list -->
            <div class="space-y-3 mb-4">
              <div v-for="v in abVariants" :key="v.id" class="rounded-lg p-3" style="border:1px solid var(--border);background:var(--surface-secondary,#f8fafc);">
                <div class="flex items-center justify-between mb-2">
                  <span class="font-semibold text-sm" style="color:#4f46e5;">變體 {{ v.name }}</span>
                  <div class="flex gap-2 items-center">
                    <span class="text-xs" style="color:var(--gray-500);">👁 {{ v.views }} / 🎯 {{ v.conversions }}</span>
                    <button class="btn btn-secondary btn-xs" @click="editVariantBlocks(v)" title="編輯區塊">✏️ 編輯</button>
                    <button class="btn btn-danger btn-xs" @click="deleteVariant(v.id)" title="刪除">✕</button>
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-xs" style="color:var(--gray-600);">流量比例：</span>
                  <input type="number" min="0" max="100" v-model.number="v.traffic_pct"
                    class="input" style="width:70px;padding:4px 8px;font-size:.82rem;"
                    @change="saveVariantPct(v)" />
                  <span class="text-xs" style="color:var(--gray-500);">%</span>
                </div>
              </div>
              <div v-if="!abVariants.length" class="text-center py-6" style="color:var(--gray-400);font-size:.85rem;">
                尚未建立任何變體，點下方按鈕新增
              </div>
            </div>

            <!-- Add variant -->
            <button v-if="abVariants.length < 4" class="btn btn-secondary btn-sm w-full" @click="addVariant">
              + 新增變體
            </button>
          </template>
        </div>

        <!-- Submissions tab -->
        <div v-if="drawerTab === 'subs'" class="p-5 overflow-y-auto flex-1 space-y-3">
          <div v-if="submissionsLoading" class="text-center py-8" style="color:var(--gray-400);">載入中…</div>
          <div v-else-if="!submissions.length" class="text-center py-8 space-y-2">
            <div style="font-size:1.75rem;color:var(--gray-300);">📭</div>
            <p style="color:var(--gray-500);">尚無表單提交記錄</p>
          </div>
          <template v-else>
            <div class="text-sm font-medium mb-2" style="color:var(--gray-600);">共 {{ submissions.length }} 筆</div>
            <div v-for="s in submissions" :key="s.id" class="rounded-lg p-4 space-y-2" style="background:var(--surface-secondary,var(--gray-50));border:1px solid var(--border);">
              <div class="flex items-center justify-between">
                <span class="text-xs font-medium" style="color:var(--gray-500);">#{{ s.id }}</span>
                <span class="text-xs" style="color:var(--gray-400);">{{ fmtDateTime(s.created_at) }}</span>
              </div>
              <div v-for="(val, key) in parseData(s.data)" :key="key" class="text-sm flex gap-2">
                <span class="font-medium" style="color:var(--gray-600);min-width:80px;">{{ key }}</span>
                <span style="color:var(--gray-800);">{{ val }}</span>
              </div>
              <div v-if="s.contact_id" class="text-xs" style="color:var(--brand-500);">已關聯聯絡人 #{{ s.contact_id }}</div>
            </div>
          </template>
        </div>

        <!-- Export button (submissions tab only) -->
        <div v-if="drawerTab === 'subs' && submissions.length" class="p-4" style="border-top:1px solid var(--border);flex-shrink:0;">
          <button class="btn btn-secondary btn-sm w-full" @click="exportSubmissionsCsv">匯出 CSV</button>
        </div>
      </div>
    </div>

    <!-- ─────────────── Delete Confirm ─────────────── -->
    <div v-if="deleteTarget" class="fixed inset-0 flex items-center justify-center z-50" style="background:rgba(0,0,0,0.45);">
      <div class="card p-6 space-y-4" style="width:380px;">
        <h3 class="font-semibold">確認刪除</h3>
        <p class="text-sm" style="color:var(--gray-600);">
          確定要刪除「<strong>{{ deleteTarget.title }}</strong>」嗎？<br>
          所有相關表單提交記錄也會一併刪除，且無法復原。
        </p>
        <div class="flex justify-end gap-3">
          <button class="btn btn-secondary" @click="deleteTarget = null">取消</button>
          <button class="btn btn-sm" style="background:var(--color-danger);color:#fff;border:none;" :disabled="deleting" @click="doDelete">
            {{ deleting ? '刪除中…' : '確認刪除' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { useAppStore, ADMIN_API } from '../store/app.js'
import LandingBlockEditor from '../components/LandingBlockEditor.vue'
import LandingStatsChart from '../components/LandingStatsChart.vue'

const store = useAppStore()

// ── 模板定義 ─────────────────────────────────────────────────
const _mkForm = (btnColor = '#4f46e5') => ({
  type: 'form',
  props: {
    title: '立即報名 / 聯絡我們', submit_text: '送出', btn_color: btnColor,
    success_message: '感謝您！我們將盡快與您聯繫。',
    bg_color: '#f9fafb', padding_y: 60,
    fields: [
      { name: 'name',    label: '姓名',  type: 'text',     required: true,  placeholder: '請輸入姓名' },
      { name: 'email',   label: 'Email', type: 'email',    required: true,  placeholder: 'you@example.com' },
      { name: 'phone',   label: '電話',  type: 'tel',      required: false, placeholder: '' },
      { name: 'message', label: '留言',  type: 'textarea', required: false, placeholder: '' }
    ]
  }
})

const LP_TEMPLATES = [
  {
    id: 'empty',
    icon: '⬜',
    name: '完全空白',
    desc: '從一張白紙開始，完全自由',
    segments: [
      { color: '#f1f5f9', h: 110, label: '（空白）', op: 0.6 }
    ],
    blocks: []
  },
  {
    id: 'edm',
    icon: '📣',
    name: 'EDM 行銷推廣',
    desc: '優惠活動通知、電子報轉換',
    segments: [
      { color: '#7c3aed', h: 38, label: 'Hero' },
      { color: '#f3f4f6', h: 26, label: '特色' },
      { color: '#f59e0b', h: 20, label: 'CTA' },
      { color: '#fff', h: 22, label: '表單', op: 0.9 }
    ],
    blocks: [
      { type: 'hero', props: { title: '限時特惠來了！', subtitle: '把握機會，立即行動', bg_color: '#7c3aed', text_color: '#ffffff', cta_text: '立即了解', cta_url: '#lp-form', cta_color: '#f59e0b', cta_text_color: '#1f2937', padding_y: 80 } },
      { type: 'features', props: { title: '三大優勢', columns: 3, bg_color: '#f9fafb', padding_y: 60, items: [{ icon: '💰', title: '超值優惠', desc: '限時折扣，把握機會' }, { icon: '⚡', title: '快速到貨', desc: '下單即出貨' }, { icon: '🛡️', title: '品質保證', desc: '30天無條件退換' }] } },
      { type: 'cta', props: { title: '優惠即將結束！', subtitle: '剩餘名額有限，請盡快報名', btn_text: '立即搶購', btn_url: '#lp-form', bg_color: '#f59e0b', text_color: '#1f2937', btn_color: '#1f2937', btn_text_color: '#ffffff', padding_y: 50 } },
      _mkForm('#7c3aed')
    ]
  },
  {
    id: 'event',
    icon: '📅',
    name: '活動報名',
    desc: '研討會、發表會、課程招募',
    segments: [
      { color: '#1d4ed8', h: 38, label: 'Hero' },
      { color: '#ffffff', h: 20, label: '詳情', op: 0.9 },
      { color: '#f3f4f6', h: 26, label: '議程' },
      { color: '#fff', h: 22, label: '報名', op: 0.9 }
    ],
    blocks: [
      { type: 'hero', props: { title: '2026 年度峰會', subtitle: '匯聚業界領袖，共探未來趨勢', bg_color: '#1d4ed8', text_color: '#ffffff', cta_text: '立即報名', cta_url: '#lp-form', cta_color: '#f59e0b', cta_text_color: '#1f2937', padding_y: 90 } },
      { type: 'text', props: { html: '<h2>活動詳情</h2><p><strong>日期：</strong>2026 年 8 月 15 日</p><p><strong>地點：</strong>台北市信義區 XX 會議中心</p><p><strong>時間：</strong>09:00 – 18:00</p><p style="margin-top:1rem;">本次峰會將邀請多位業界專家，分享最新技術趨勢與實務案例，是不可錯過的年度盛事。</p>', align: 'left', bg_color: '#ffffff', text_color: '#1f2937', padding_y: 48 } },
      { type: 'features', props: { title: '精彩議程', columns: 3, bg_color: '#f0f4ff', padding_y: 60, items: [{ icon: '🎤', title: '主題演講', desc: '業界領袖分享洞見' }, { icon: '💡', title: '工作坊', desc: '實作演練，動手學習' }, { icon: '🤝', title: '社交交流', desc: '擴展人脈，建立合作' }] } },
      _mkForm('#1d4ed8')
    ]
  },
  {
    id: 'product',
    icon: '📦',
    name: '產品介紹',
    desc: '新產品發表、服務說明頁面',
    segments: [
      { color: '#0f172a', h: 38, label: 'Hero' },
      { color: '#f3f4f6', h: 26, label: '特色' },
      { color: '#e2e8f0', h: 18, label: '圖片' },
      { color: '#4f46e5', h: 20, label: 'CTA' },
      { color: '#fff', h: 22, label: '表單', op: 0.9 }
    ],
    blocks: [
      { type: 'hero', props: { title: '全新產品正式上市', subtitle: '重新定義效能與體驗的極致之作', bg_color: '#0f172a', text_color: '#ffffff', cta_text: '了解更多', cta_url: '#lp-features', cta_color: '#6366f1', cta_text_color: '#ffffff', padding_y: 90 } },
      { type: 'features', props: { title: '核心特色', columns: 3, bg_color: '#f8fafc', padding_y: 60, items: [{ icon: '⚡', title: '極速效能', desc: '效能提升 300%，讓工作更流暢' }, { icon: '🎨', title: '精美設計', desc: '獲獎設計，賞心悅目' }, { icon: '🔒', title: '企業級安全', desc: '軍事級加密，資料零洩漏' }] } },
      { type: 'image', props: { url: '', alt: '產品展示圖', max_width: 900, align: 'center', bg_color: '#ffffff', padding_y: 40 } },
      { type: 'cta', props: { title: '立即體驗', subtitle: '限時早鳥優惠，名額有限', btn_text: '申請試用', btn_url: '#lp-form', bg_color: '#4f46e5', text_color: '#ffffff', btn_color: '#ffffff', btn_text_color: '#4f46e5', padding_y: 60 } },
      _mkForm('#4f46e5')
    ]
  },
  {
    id: 'saas',
    icon: '🚀',
    name: 'SaaS 免費試用',
    desc: '軟體服務、訂閱方案、免費試用',
    segments: [
      { color: '#4f46e5', h: 44, label: 'Hero' },
      { color: '#f0f4ff', h: 30, label: '特色' },
      { color: '#4f46e5', h: 20, label: 'CTA' },
      { color: '#f9fafb', h: 22, label: '表單', op: 0.9 }
    ],
    blocks: [
      { type: 'hero', props: { title: '免費試用 14 天', subtitle: '無需信用卡，立即開始您的數位轉型之旅', bg_color: '#4f46e5', text_color: '#ffffff', cta_text: '免費開始', cta_url: '#lp-form', cta_color: '#f8fafc', cta_text_color: '#4f46e5', padding_y: 100 } },
      { type: 'features', props: { title: '為什麼選擇我們', columns: 3, bg_color: '#f0f4ff', padding_y: 60, items: [{ icon: '☁️', title: '雲端優先', desc: '隨時隨地，任何裝置存取' }, { icon: '📊', title: '智慧分析', desc: '即時數據儀表板，洞察業績' }, { icon: '🔗', title: '無縫整合', desc: '對接 100+ 主流工具' }] } },
      { type: 'cta', props: { title: '今天就開始免費試用', subtitle: '加入超過 10,000 家企業的行列', btn_text: '立即免費試用', btn_url: '#lp-form', bg_color: '#4f46e5', text_color: '#ffffff', btn_color: '#f8fafc', btn_text_color: '#4f46e5', padding_y: 60 } },
      { type: 'form', props: { ..._mkForm('#4f46e5').props, title: '開始免費試用', submit_text: '立即試用', fields: [{ name: 'name', label: '姓名', type: 'text', required: true, placeholder: '' }, { name: 'email', label: '公司 Email', type: 'email', required: true, placeholder: 'you@company.com' }, { name: 'company', label: '公司名稱', type: 'text', required: false, placeholder: '' }] } }
    ]
  },
  {
    id: 'course',
    icon: '🎓',
    name: '課程招生',
    desc: '線上/線下課程、工作坊招募',
    segments: [
      { color: '#065f46', h: 38, label: 'Hero' },
      { color: '#ecfdf5', h: 26, label: '課程內容' },
      { color: '#f3f4f6', h: 20, label: '講師' },
      { color: '#fff', h: 22, label: '報名', op: 0.9 }
    ],
    blocks: [
      { type: 'hero', props: { title: '專業技能培訓課程', subtitle: '從零基礎到實戰應用，30天學會核心技能', bg_color: '#064e3b', text_color: '#ffffff', cta_text: '立即報名', cta_url: '#lp-form', cta_color: '#10b981', cta_text_color: '#ffffff', padding_y: 90 } },
      { type: 'features', props: { title: '課程亮點', columns: 3, bg_color: '#ecfdf5', padding_y: 60, items: [{ icon: '📚', title: '系統化課綱', desc: '循序漸進，扎實學習' }, { icon: '🎯', title: '實戰演練', desc: '專案導向，學以致用' }, { icon: '🏆', title: '結業證書', desc: '通過考核取得認證' }] } },
      { type: 'text', props: { html: '<h2>師資介紹</h2><p>我們的講師均擁有 10 年以上產業實務經驗，曾服務於國內外知名企業，課程內容貼近實際工作需求。</p>', align: 'center', bg_color: '#f9fafb', text_color: '#1f2937', padding_y: 48 } },
      { type: 'form', props: { ..._mkForm('#10b981').props, title: '立即報名', submit_text: '送出報名', bg_color: '#ffffff', btn_color: '#059669', fields: [{ name: 'name', label: '姓名', type: 'text', required: true, placeholder: '' }, { name: 'email', label: 'Email', type: 'email', required: true, placeholder: '' }, { name: 'phone', label: '手機', type: 'text', required: true, placeholder: '' }, { name: 'experience', label: '相關經驗', type: 'text', required: false, placeholder: '例：完全新手 / 有基礎' }] } }
    ]
  },
  {
    id: 'lead',
    icon: '🎯',
    name: '潛在客戶開發',
    desc: '白皮書下載、名單收集、免費諮詢',
    segments: [
      { color: '#0369a1', h: 48, label: 'Hero' },
      { color: '#f0f9ff', h: 24, label: '價值說明' },
      { color: '#fff', h: 34, label: '表單', op: 0.9 }
    ],
    blocks: [
      { type: 'hero', props: { title: '免費下載：2026 產業趨勢報告', subtitle: '填寫表單即可獲得 40 頁深度分析報告', bg_color: '#075985', text_color: '#ffffff', cta_text: '立即索取', cta_url: '#lp-form', cta_color: '#38bdf8', cta_text_color: '#0c4a6e', padding_y: 100 } },
      { type: 'features', props: { title: '報告涵蓋內容', columns: 3, bg_color: '#f0f9ff', padding_y: 60, items: [{ icon: '📈', title: '市場趨勢', desc: '2026 關鍵數據與預測' }, { icon: '🔍', title: '競爭分析', desc: '主要競爭者策略解析' }, { icon: '💡', title: '行動建議', desc: '可立即執行的策略清單' }] } },
      { type: 'form', props: { ..._mkForm('#0369a1').props, title: '索取免費報告', submit_text: '立即下載', bg_color: '#ffffff', btn_color: '#0284c7', fields: [{ name: 'name', label: '姓名', type: 'text', required: true, placeholder: '' }, { name: 'email', label: '公司 Email', type: 'email', required: true, placeholder: '' }, { name: 'company', label: '公司 / 單位', type: 'text', required: true, placeholder: '' }, { name: 'title', label: '職稱', type: 'text', required: false, placeholder: '' }] } }
    ]
  },
  {
    id: 'ecommerce',
    icon: '🛒',
    name: '電商促銷',
    desc: '限時折扣、購物節、清倉特賣',
    segments: [
      { color: '#dc2626', h: 38, label: 'Hero' },
      { color: '#fef2f2', h: 22, label: '商品' },
      { color: '#fee2e2', h: 18, label: '優惠' },
      { color: '#dc2626', h: 18, label: 'CTA' },
      { color: '#fff', h: 10, label: '表單', op: 0.9 }
    ],
    blocks: [
      { type: 'hero', props: { title: '年中慶 全館 5 折起', subtitle: '限時 72 小時，超過 500 款商品破盤優惠', bg_color: '#dc2626', text_color: '#ffffff', cta_text: '立即搶購', cta_url: '#lp-form', cta_color: '#fbbf24', cta_text_color: '#1f2937', padding_y: 80 } },
      { type: 'features', props: { title: '本檔活動亮點', columns: 3, bg_color: '#fef2f2', padding_y: 50, items: [{ icon: '🔥', title: '超低折扣', desc: '最低 2 折，錯過等一年' }, { icon: '🚚', title: '免運費', desc: '滿 $500 免運，快速到貨' }, { icon: '🎁', title: '加購好禮', desc: '指定商品加購享優惠' }] } },
      { type: 'cta', props: { title: '倒數計時！優惠即將結束', subtitle: '不要讓自己後悔，現在就下手！', btn_text: '搶購去', btn_url: '#lp-form', bg_color: '#fbbf24', text_color: '#1f2937', btn_color: '#dc2626', btn_text_color: '#ffffff', padding_y: 50 } },
      { type: 'form', props: { ..._mkForm('#dc2626').props, title: '訂閱搶先知折扣', submit_text: '訂閱優惠通知', bg_color: '#fff', btn_color: '#dc2626', fields: [{ name: 'name', label: '姓名', type: 'text', required: true, placeholder: '' }, { name: 'email', label: 'Email', type: 'email', required: true, placeholder: '' }] } }
    ]
  },
  {
    id: 'webinar',
    icon: '🖥️',
    name: '線上講座',
    desc: 'Webinar、直播、線上說明會',
    segments: [
      { color: '#7e22ce', h: 40, label: 'Hero' },
      { color: '#faf5ff', h: 28, label: '議題' },
      { color: '#f3f4f6', h: 16, label: '講師' },
      { color: '#fff', h: 22, label: '報名', op: 0.9 }
    ],
    blocks: [
      { type: 'hero', props: { title: '免費線上講座', subtitle: '60 分鐘，帶你掌握核心趨勢與實作心法', bg_color: '#581c87', text_color: '#ffffff', cta_text: '免費報名', cta_url: '#lp-form', cta_color: '#c084fc', cta_text_color: '#1f2937', padding_y: 90 } },
      { type: 'features', props: { title: '本次講座重點', columns: 3, bg_color: '#faf5ff', padding_y: 60, items: [{ icon: '🧠', title: '趨勢洞察', desc: '最新市場動態一次掌握' }, { icon: '🛠️', title: '工具實演', desc: '現場示範實用工具操作' }, { icon: '❓', title: '現場 Q&A', desc: '直接向專家提問解惑' }] } },
      { type: 'text', props: { html: '<h2 style="text-align:center;">主講人</h2><p style="text-align:center;">資深產業顧問・10 年以上輔導經驗・曾服務超過 200 家企業</p>', align: 'center', bg_color: '#f9fafb', text_color: '#1f2937', padding_y: 40 } },
      { type: 'form', props: { ..._mkForm('#7e22ce').props, title: '立即免費報名', submit_text: '確認報名', bg_color: '#ffffff', btn_color: '#7c3aed', fields: [{ name: 'name', label: '姓名', type: 'text', required: true, placeholder: '' }, { name: 'email', label: 'Email', type: 'email', required: true, placeholder: '' }, { name: 'phone', label: '手機', type: 'text', required: false, placeholder: '' }] } }
    ]
  },
  {
    id: 'consulting',
    icon: '💼',
    name: '免費諮詢預約',
    desc: '顧問服務、健診評估、預約體驗',
    segments: [
      { color: '#1e293b', h: 40, label: 'Hero' },
      { color: '#f8fafc', h: 24, label: '服務內容' },
      { color: '#fff', h: 42, label: '預約表單', op: 0.9 }
    ],
    blocks: [
      { type: 'hero', props: { title: '預約免費諮詢', subtitle: '30 分鐘一對一，為您量身規劃最佳方案', bg_color: '#0f172a', text_color: '#ffffff', cta_text: '立即預約', cta_url: '#lp-form', cta_color: '#38bdf8', cta_text_color: '#0f172a', padding_y: 90 } },
      { type: 'features', props: { title: '諮詢服務包含', columns: 3, bg_color: '#f8fafc', padding_y: 60, items: [{ icon: '🔍', title: '現況診斷', desc: '深入了解您的需求與痛點' }, { icon: '📋', title: '方案規劃', desc: '客製化建議與執行路徑' }, { icon: '🤝', title: '後續追蹤', desc: '諮詢後持續提供支援' }] } },
      { type: 'form', props: { ..._mkForm('#1e293b').props, title: '填寫預約資訊', submit_text: '送出預約', bg_color: '#ffffff', btn_color: '#0f172a', fields: [{ name: 'name', label: '姓名', type: 'text', required: true, placeholder: '' }, { name: 'email', label: 'Email', type: 'email', required: true, placeholder: '' }, { name: 'phone', label: '聯絡電話', type: 'text', required: true, placeholder: '' }, { name: 'message', label: '諮詢主題', type: 'text', required: false, placeholder: '例：想了解導入 AI 的可行性' }] } }
    ]
  },
  {
    id: 'blank',
    icon: '📝',
    name: '基本版型',
    desc: 'Hero + 表單，快速客製',
    segments: [
      { color: '#e2e8f0', h: 50, label: 'Hero' },
      { color: '#f8fafc', h: 60, label: '表單', op: 0.8 }
    ],
    blocks: [
      { type: 'hero', props: { title: '您的標題', subtitle: '副標題文字', bg_color: '#1e293b', text_color: '#ffffff', cta_text: '', cta_url: '#lp-form', cta_color: '#4f46e5', cta_text_color: '#ffffff', padding_y: 80 } },
      _mkForm('#4f46e5')
    ]
  },
  {
    id: 'campaign',
    icon: '📧',
    name: '從行銷活動產生',
    desc: '以現有 EDM 活動內容為基礎快速建立（需選擇來源活動）',
    segments: [
      { color: '#0ea5e9', h: 32, label: '活動主旨' },
      { color: '#f0f9ff', h: 44, label: 'EDM 內容' },
      { color: '#f9fafb', h: 30, label: '表單', op: 0.9 }
    ],
    blocks: []
  }
]

// ── State ──────────────────────────────────────────────────
const pages = ref([])
const campaigns = ref([])
const loading = ref(false)
const saving = ref(false)
const deleting = ref(false)

const showSubmissions = ref(false)
const selectedPage = ref(null)
const submissions = ref([])
const submissionsLoading = ref(false)
const drawerTab = ref('stats')
const pageStats = ref({})
const pageAnalytics = ref({})
const statsLoading = ref(false)
const statsPeriod = ref(7)
// A/B testing state
const abEnabled = ref(false)
const abVariants = ref([])
const abLoading = ref(false)

const deleteTarget = ref(null)

// ── Template Picker ──────────────────────────────────────────
const showTemplatePicker = ref(false)
const pickerMode = ref('template') // 'template' | 'ai'
const pickerSelectedId = ref('edm')
const pickerCampaignId = ref('')
const newPageTitle = ref('')
const creatingPage = ref(false)

// AI generate fields
const aiDescription = ref('')
const aiTargetAudience = ref('')
const aiTone = ref('professional')

const openCreate = () => {
  newPageTitle.value = ''
  pickerMode.value = 'template'
  pickerSelectedId.value = 'edm'
  pickerCampaignId.value = ''
  aiDescription.value = ''
  aiTargetAudience.value = ''
  aiTone.value = 'professional'
  showTemplatePicker.value = true
}

const createFromTemplate = async () => {
  if (!newPageTitle.value.trim()) return
  creatingPage.value = true
  try {
    let pageId, pageSlug, pageTitle, initialBlocks

    if (pickerSelectedId.value === 'campaign') {
      // Generate from campaign EDM content
      if (!pickerCampaignId.value) { window.$toast?.error('請選擇來源活動'); return }
      const genRes = await axios.post(
        `${ADMIN_API}/ai/marketing/landing/generate`,
        null,
        { params: { campaign_id: pickerCampaignId.value, title: newPageTitle.value.trim() }, headers: authHeader() }
      )
      pageId = genRes.data.id
      pageSlug = genRes.data.slug
      pageTitle = genRes.data.title
      const campaignHtml = genRes.data.content_html || ''
      initialBlocks = [
        { type: 'hero', props: { title: newPageTitle.value.trim(), subtitle: '', bg_color: '#0ea5e9', text_color: '#ffffff', cta_text: '了解更多', cta_url: '#lp-form', cta_color: '#f8fafc', cta_text_color: '#0ea5e9', padding_y: 80 } },
        { type: 'html', props: { code: campaignHtml, padding_y: 0 } },
        { type: 'form', props: { ..._mkForm('#0ea5e9').props } }
      ]
    } else {
      const createPayload = { title: newPageTitle.value.trim() }
      if (pickerCampaignId.value) createPayload.campaign_id = Number(pickerCampaignId.value)
      const createRes = await axios.post(
        `${ADMIN_API}/ai/marketing/landing/`,
        createPayload,
        { headers: authHeader() }
      )
      pageId = createRes.data.id
      pageSlug = createRes.data.slug
      pageTitle = createRes.data.title
      const tpl = LP_TEMPLATES.find(t => t.id === pickerSelectedId.value)
      // Deep clone blocks to avoid mutating template definitions
      initialBlocks = JSON.parse(JSON.stringify(tpl?.blocks || []))
      // Patch hero title to match page title
      const heroBlock = initialBlocks.find(b => b.type === 'hero')
      if (heroBlock) heroBlock.props.title = pageTitle
    }

    showTemplatePicker.value = false
    openBlockEditor({ id: pageId, title: pageTitle, slug: pageSlug, initialBlocks })
    await load()
  } catch (e) {
    window.$toast?.error('建立失敗', e.response?.data?.detail || e.message)
  } finally {
    creatingPage.value = false
  }
}

const createWithAI = async () => {
  if (!newPageTitle.value.trim() || !aiDescription.value.trim()) return
  creatingPage.value = true
  try {
    const payload = {
      title: newPageTitle.value.trim(),
      description: aiDescription.value.trim(),
      target_audience: aiTargetAudience.value.trim(),
      tone: aiTone.value,
      campaign_id: pickerCampaignId.value ? Number(pickerCampaignId.value) : null,
      language: 'zh-TW'
    }
    const res = await axios.post(
      `${ADMIN_API}/ai/marketing/landing/ai-generate`,
      payload,
      { headers: authHeader() }
    )
    const page = res.data
    showTemplatePicker.value = false
    openBlockEditor({ id: page.id, title: page.title, slug: page.slug, initialBlocks: [] })
    await load()
  } catch (e) {
    window.$toast?.error('AI 生成失敗', e.response?.data?.detail || e.message)
  } finally {
    creatingPage.value = false
  }
}

// ── Block Editor ─────────────────────────────────────────────
const blockEditorPage = ref(null) // { id, title, slug, initialBlocks? }

const openBlockEditor = (p) => {
  blockEditorPage.value = { id: p.id, title: p.title, slug: p.slug, initialBlocks: p.initialBlocks || [] }
}
const closeBlockEditor = () => {
  blockEditorPage.value = null
}

// ── Computed ────────────────────────────────────────────────
const totalViews = computed(() => pages.value.reduce((s, p) => s + (p.views || 0), 0))
const totalConversions = computed(() => pages.value.reduce((s, p) => s + (p.conversions || 0), 0))
const avgConvRate = computed(() => {
  const withViews = pages.value.filter(p => (p.views || 0) > 0)
  if (!withViews.length) return 0
  return withViews.reduce((s, p) => s + convRate(p), 0) / withViews.length
})

// ── Helpers ─────────────────────────────────────────────────
const token = () => localStorage.getItem('token') || ''
const authHeader = () => ({ Authorization: `Bearer ${token()}` })

const convRate = (p) => {
  const v = p.views || 0
  return v > 0 ? Math.min(100, ((p.conversions || 0) / v) * 100) : 0
}

const publicUrl = (p) => `${ADMIN_API}/p/${p.slug}`

const fmtDate = (ts) => {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' })
}
const fmtDateTime = (ts) => {
  if (!ts) return '—'
  return new Date(ts).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

const parseData = (jsonStr) => {
  try { return JSON.parse(jsonStr || '{}') } catch { return {} }
}

const copyUrl = async (p) => {
  const url = publicUrl(p)
  try {
    await navigator.clipboard.writeText(url)
    window.$toast?.success('已複製', url)
  } catch {
    window.$toast?.info('連結', url)
  }
}

// ── Load ────────────────────────────────────────────────────
const load = async () => {
  loading.value = true
  try {
    const [pRes, cRes] = await Promise.all([
      axios.get(`${ADMIN_API}/ai/marketing/landing/`, { headers: authHeader() }),
      axios.get(`${ADMIN_API}/ai/marketing/campaigns`, { params: { skip: 0, limit: 200 }, headers: authHeader() })
    ])
    pages.value = Array.isArray(pRes.data) ? pRes.data : []
    campaigns.value = (Array.isArray(cRes.data) ? cRes.data : []).filter(c =>
      ['sent', 'sending', 'completed', 'processing'].includes(c.status) || Number(c.total_recipients) > 0
    )
  } catch (e) {
    window.$toast?.error('載入失敗', e.response?.data?.detail || e.message)
  } finally {
    loading.value = false
  }
}

onMounted(load)


// ── Delete ──────────────────────────────────────────────────
const confirmDelete = (p) => { deleteTarget.value = p }

const doDelete = async () => {
  if (!deleteTarget.value) return
  deleting.value = true
  try {
    await axios.delete(`${ADMIN_API}/ai/marketing/landing/${deleteTarget.value.id}`, { headers: authHeader() })
    window.$toast?.success('已刪除')
    deleteTarget.value = null
    await load()
  } catch (e) {
    window.$toast?.error('刪除失敗', e.response?.data?.detail || e.message)
  } finally {
    deleting.value = false
  }
}

// ── Submissions ─────────────────────────────────────────────
const loadStats = async (pageId, days) => {
  statsLoading.value = true
  try {
    const [statsRes, anaRes] = await Promise.all([
      axios.get(`${ADMIN_API}/ai/marketing/landing/${pageId}/stats`, { params: { days }, headers: authHeader() }),
      axios.get(`${ADMIN_API}/ai/marketing/landing/${pageId}/analytics`, { params: { days: Math.max(30, days) }, headers: authHeader() }).catch(() => ({ data: {} })),
    ])
    pageStats.value = statsRes.data
    pageAnalytics.value = anaRes.data || {}
  } catch (e) {
    pageStats.value = {}
    pageAnalytics.value = {}
  } finally {
    statsLoading.value = false
  }
}

const switchPeriod = (days) => {
  statsPeriod.value = days
  if (selectedPage.value) loadStats(selectedPage.value.id, days)
}

// ── A/B Testing ──────────────────────────────────────────────
const loadVariants = async (pageId) => {
  abLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/landing/${pageId}/variants`, { headers: authHeader() })
    abVariants.value = Array.isArray(res.data) ? res.data : []
  } catch { abVariants.value = [] } finally { abLoading.value = false }
}

const openAbTab = async () => {
  drawerTab.value = 'ab'
  if (selectedPage.value) {
    abEnabled.value = !!selectedPage.value.ab_enabled
    await loadVariants(selectedPage.value.id)
  }
}

const toggleAbEnabled = async () => {
  if (!selectedPage.value) return
  try {
    await axios.put(`${ADMIN_API}/ai/marketing/landing/${selectedPage.value.id}`,
      { ab_enabled: abEnabled.value }, { headers: authHeader() })
    selectedPage.value.ab_enabled = abEnabled.value
  } catch (e) {
    abEnabled.value = !abEnabled.value
    window.$toast?.error('設定失敗', e.message)
  }
}

const addVariant = async () => {
  if (!selectedPage.value) return
  const names = ['A', 'B', 'C', 'D']
  const usedNames = abVariants.value.map(v => v.name)
  const nextName = names.find(n => !usedNames.includes(n)) || 'A'
  try {
    const res = await axios.post(
      `${ADMIN_API}/ai/marketing/landing/${selectedPage.value.id}/variants`,
      { name: nextName, traffic_pct: 50, blocks_json: null },
      { headers: authHeader() }
    )
    abVariants.value.push(res.data)
    window.$toast?.success(`已新增變體 ${nextName}`)
  } catch (e) {
    window.$toast?.error('新增失敗', e.response?.data?.detail || e.message)
  }
}

const saveVariantPct = async (v) => {
  if (!selectedPage.value) return
  try {
    await axios.put(
      `${ADMIN_API}/ai/marketing/landing/${selectedPage.value.id}/variants/${v.id}`,
      { traffic_pct: v.traffic_pct }, { headers: authHeader() }
    )
  } catch (e) { window.$toast?.error('儲存失敗', e.message) }
}

const deleteVariant = async (variantId) => {
  if (!selectedPage.value || !confirm('確定刪除此變體？')) return
  try {
    await axios.delete(
      `${ADMIN_API}/ai/marketing/landing/${selectedPage.value.id}/variants/${variantId}`,
      { headers: authHeader() }
    )
    abVariants.value = abVariants.value.filter(v => v.id !== variantId)
  } catch (e) { window.$toast?.error('刪除失敗', e.message) }
}

const editVariantBlocks = (v) => {
  if (!selectedPage.value) return
  // Open block editor for variant: use variant's blocks
  const variantBlocks = v.blocks_json ? JSON.parse(v.blocks_json) : []
  blockEditorPage.value = {
    id: selectedPage.value.id,
    title: `${selectedPage.value.title} — 變體 ${v.name}`,
    slug: selectedPage.value.slug,
    initialBlocks: variantBlocks,
    variantId: v.id
  }
}

const openSubmissions = async (p) => {
  selectedPage.value = p
  drawerTab.value = 'stats'
  statsPeriod.value = 7
  showSubmissions.value = true
  submissions.value = []
  pageStats.value = {}
  pageAnalytics.value = {}
  // Load both in parallel
  submissionsLoading.value = true
  loadStats(p.id, 7)
  axios.get(`${ADMIN_API}/ai/marketing/landing/${p.id}/submissions`, { headers: authHeader() })
    .then(res => { submissions.value = Array.isArray(res.data) ? res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : [] })
    .catch(e => { window.$toast?.error('載入提交記錄失敗', e.message) })
    .finally(() => { submissionsLoading.value = false })
}

const exportSubmissionsCsv = () => {
  if (!submissions.value.length) return
  const allKeys = new Set()
  submissions.value.forEach(s => Object.keys(parseData(s.data)).forEach(k => allKeys.add(k)))
  const keys = [...allKeys]
  const headers = ['id', 'created_at', 'contact_id', ...keys]
  const rows = submissions.value.map(s => {
    const d = parseData(s.data)
    return [s.id, s.created_at, s.contact_id || '', ...keys.map(k => d[k] ?? '')].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
  })
  const csv = [headers.join(','), ...rows].join('\n')
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `submissions_${selectedPage.value?.id || 'all'}_${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
}
</script>

<style scoped>
.tpl-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}
.tpl-card {
  border: 2px solid var(--border, #e2e8f0);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s;
  position: relative;
  background: #fff;
}
.tpl-card:hover { border-color: #818cf8; box-shadow: 0 2px 12px rgba(79,70,229,.12); }
.tpl-card-selected { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79,70,229,.15); }
.tpl-tower {
  display: flex;
  flex-direction: column;
  height: 110px;
  overflow: hidden;
}
.tpl-seg {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.tpl-seg-label {
  font-size: .6rem;
  font-weight: 600;
  color: rgba(0,0,0,.35);
  letter-spacing: .03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80%;
}
.tpl-info {
  padding: 10px 12px 12px;
  border-top: 1px solid #f1f5f9;
}
.tpl-icon { font-size: 1.25rem; margin-bottom: 4px; }
.tpl-name { font-size: .85rem; font-weight: 700; color: #1e293b; margin-bottom: 3px; }
.tpl-desc { font-size: .72rem; color: #64748b; line-height: 1.4; }
.tpl-check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  background: #4f46e5;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: .75rem;
  font-weight: 700;
}
.drawer-tab {
  padding: 6px 16px;
  border: none;
  background: none;
  font-size: .82rem;
  font-weight: 500;
  color: #94a3b8;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color .12s, border-color .12s;
}
.drawer-tab.active {
  color: #4f46e5;
  border-bottom-color: #4f46e5;
}
.drawer-tab:hover:not(.active) { color: #475569; }
/* A/B toggle switch */
.lp-toggle { position: relative; display: inline-block; width: 44px; height: 24px; }
.lp-toggle input { opacity: 0; width: 0; height: 0; }
.lp-toggle-slider {
  position: absolute; inset: 0; background: #cbd5e1; border-radius: 24px; cursor: pointer;
  transition: background .2s;
}
.lp-toggle-slider::before {
  content: ''; position: absolute; width: 18px; height: 18px; background: #fff;
  border-radius: 50%; left: 3px; top: 3px; transition: transform .2s;
}
.lp-toggle input:checked + .lp-toggle-slider { background: #4f46e5; }
.lp-toggle input:checked + .lp-toggle-slider::before { transform: translateX(20px); }
/* btn-xs */
.btn-xs { padding: 3px 8px; font-size: .72rem; }
.btn-danger { background: #fee2e2; color: #dc2626; border: 1px solid #fca5a5; }
.btn-danger:hover { background: #fecaca; }

/* picker mode tabs */
.tpl-mode-tabs { display: flex; gap: 0; border: 1px solid var(--border); border-radius: 8px; overflow: hidden; width: fit-content; }
.tpl-mode-btn {
  padding: 7px 20px; font-size: .85rem; font-weight: 500;
  background: transparent; border: none; cursor: pointer;
  color: var(--gray-500); transition: all .15s;
}
.tpl-mode-btn.active { background: var(--primary); color: #fff; }
.tpl-mode-btn:not(.active):hover { background: var(--gray-100); }

/* AI hint box */
.tpl-ai-hint {
  background: #eff6ff; border: 1px solid #bfdbfe;
  border-radius: 6px; padding: 10px 14px;
  font-size: .8rem; color: #1d4ed8; line-height: 1.5;
}

/* btn-ai */
.btn-ai {
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  color: #fff; border: none; border-radius: 6px;
  padding: 8px 18px; font-size: .875rem; cursor: pointer;
  transition: opacity .15s;
}
.btn-ai:hover:not(:disabled) { opacity: .9; }
.btn-ai:disabled { opacity: .5; cursor: not-allowed; }
</style>
